import { useMemo, type ComponentType } from "react";
import { Plus, Compass, Shuffle, Sun, Moon, CloudMoon, BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { DailyQuote } from "@/components/home/DailyQuote";
import { slateCardClass, heroCtaClass } from "@/lib/card-styles";
import { typeCaptionClass, typeMetaClass } from "@/lib/typography";
import { cn } from "@/lib/utils";
import type { TFunction } from "i18next";

interface HomeHeroProps {
    isAuthenticated: boolean;
    displayName?: string | null;
    onCreateTopic?: () => void;
    onBrowseTopics?: () => void;
    /** Jump into the shuffled /random-question drill. */
    onRandomQuestion?: () => void;
    className?: string;
}

/**
 * Time-of-day salutation.
 *
 * The hour buckets are the same everywhere but the *words* are not — English
 * says "Good evening" at 18:00 and Vietnamese says "Chào buổi tối", and a
 * language with no such distinction just gets the default. Keyed by the same
 * buckets in both locales rather than computed from the hour, because the
 * bucket boundaries are a language decision, not an astronomy one.
 */
const getGreeting = (t: TFunction): { greeting: string; Icon: ComponentType<{ className?: string }>; tone: string } => {
    const hour = new Date().getHours();
    if (hour < 5) return { greeting: t("home.hero.greeting.lateNight", "Burning the midnight oil"), Icon: Moon, tone: "text-stone-400 dark:text-stone-300" };
    if (hour < 12) return { greeting: t("home.hero.greeting.morning", "Good morning"), Icon: Sun, tone: "text-amber-500 dark:text-amber-400" };
    if (hour < 17) return { greeting: t("home.hero.greeting.afternoon", "Good afternoon"), Icon: Sun, tone: "text-amber-500 dark:text-amber-400" };
    if (hour < 22) return { greeting: t("home.hero.greeting.evening", "Good evening"), Icon: CloudMoon, tone: "text-blue-500 dark:text-blue-400" };
    return { greeting: t("home.hero.greeting.night", "Welcome back"), Icon: Moon, tone: "text-stone-400 dark:text-stone-300" };
};

const HomeHero = ({
    isAuthenticated,
    displayName,
    onCreateTopic,
    onBrowseTopics,
    onRandomQuestion,
    className,
}: HomeHeroProps) => {
    const { t } = useTranslation();
    const { greeting, Icon: GreetingIcon, tone } = useMemo(() => getGreeting(t), [t]);
    const rawName = displayName?.split(" ")[0] || (isAuthenticated ? t("home.hero.defaultName", "there") : t("home.hero.guestName", "friend"));
    // Generated ids (browser-test-1787283850882) are not a greeting. Keep the
    // salutation as the headline and let the name be quiet attribution under
    // it, so the hero reads the same for every account.
    const title = isAuthenticated ? greeting : t("home.hero.welcomeTitle", "Welcome to Lyder Talk");
    const nameLine = isAuthenticated ? rawName : null;

    const description = t("home.hero.description", "Pick a topic, practise speaking, and build a daily streak.");

    return (
        <div
            data-tour="home-hero"
            className={cn(
                "relative overflow-hidden",
                slateCardClass,
                // Signed-in learners get the compact header: presence and the
                // day's strip below are the page's job, and a 300px greeting
                // pushes the resume list under the fold. Guests keep the
                // roomier version, which doubles as the landing hero.
                isAuthenticated ? "p-4 sm:p-5 lg:p-6" : "p-5 sm:p-7 lg:p-9",
                // Warm paper with a cool wash in the far corner, so the card has
                // depth without a second surface competing for attention.
                "bg-[linear-gradient(135deg,hsl(38_52%_97%)_0%,hsl(var(--background))_38%,hsl(214_80%_96%)_100%)]",
                "dark:bg-[linear-gradient(135deg,hsl(24_12%_13%)_0%,hsl(var(--background))_38%,hsl(222_30%_14%)_100%)]",
                className
            )}
        >
            {/* Single warm light source, top-left, echoing the greeting icon. */}
            <div
                className="pointer-events-none absolute -left-24 -top-28 h-64 w-64 rounded-full bg-amber-300/25 blur-3xl dark:bg-amber-500/10"
                aria-hidden="true"
            />
            {/* Dot grid, faded in from the right. Was opacity 0.35 which read as
                a smudge across the whole card; masked to the edge it gives the
                hero texture instead of noise. */}
            <div
                className="pointer-events-none absolute inset-y-0 right-0 w-2/5 opacity-60 dark:opacity-30"
                aria-hidden="true"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 1px 1px, hsl(var(--muted-foreground) / 0.22) 1px, transparent 0)",
                    backgroundSize: "16px 16px",
                    maskImage: "linear-gradient(to right, transparent, black 60%)",
                    WebkitMaskImage: "linear-gradient(to right, transparent, black 60%)",
                }}
            />

            <div className="relative flex flex-col gap-4 sm:gap-6 lg:flex-row lg:items-end lg:gap-12">
                <div className="min-w-0 space-y-2 sm:space-y-2.5 lg:max-w-[42rem]">
                    {/* Educational study-desk eyebrow */}
                    <p
                        className={cn(
                            typeMetaClass,
                            // Block-level, not inline-flex: an inline eyebrow
                            // shares a line with the inline-flex <h1> below it,
                            // which on a phone read "SPEAKING PRACTICE ☀ Good
                            // morning" as one run.
                            "flex w-fit items-center gap-1.5 text-primary/90 dark:text-blue-400"
                        )}
                    >
                        <BookOpen className="h-3 w-3" aria-hidden="true" />
                        <span>{t("pages.topics.metaLabel", "Speaking Practice")}</span>
                    </p>

                    {/* Serif display per the type scale — the greeting is the one
                        editorial voice on the page, and Fraunces gives it warmth
                        the bold-sans version never had. */}
                    <h1
                        className={cn(
                            "inline-flex flex-wrap items-baseline gap-x-3 gap-y-1",
                            "font-display",
                            isAuthenticated
                                ? "text-2xl sm:text-3xl lg:text-4xl"
                                : "text-3xl sm:text-4xl lg:text-5xl",
                            "font-semibold",
                            "tracking-tight leading-[1.08] text-balance",
                            "text-stone-900 dark:text-stone-50"
                        )}
                    >
                        <GreetingIcon
                            className={cn(
                                "h-6 w-6 sm:h-8 sm:w-8 shrink-0 translate-y-0.5",
                                tone
                            )}
                            aria-hidden="true"
                        />
                        <span className="break-words [overflow-wrap:anywhere]">{title}</span>
                    </h1>
                    {nameLine && (
                        <p
                            className={cn(
                                "break-words [overflow-wrap:anywhere]",
                                typeCaptionClass
                            )}
                        >
                            {nameLine}
                        </p>
                    )}
                    <p
                        className={cn(
                            "text-stone-500 dark:text-stone-400 max-w-2xl leading-relaxed text-pretty",
                            "text-xs sm:text-base"
                        )}
                    >
                        {description}
                    </p>

                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 pt-0 sm:pt-1">
                        {onCreateTopic && (
                            <Button
                                data-tour="create-topic"
                                size="sm"
                                onClick={onCreateTopic}
                                className={heroCtaClass}
                            >
                                <Plus className="w-3.5 h-3.5" />
                                {t("home.hero.createTopic", "Create Topic")}
                            </Button>
                        )}
                        {onBrowseTopics && (
                            <Button
                                data-tour="browse-topics"
                                size="sm"
                                variant="outline"
                                onClick={onBrowseTopics}
                                className="gap-1.5 rounded-full text-xs px-4 h-9"
                            >
                                <Compass className="w-3.5 h-3.5" />
                                {t("home.hero.browseTopics", "Browse Topics")}
                            </Button>
                        )}
                        {onRandomQuestion && (
                            <Button
                                data-tour="random-question"
                                size="sm"
                                variant="ghost"
                                onClick={onRandomQuestion}
                                className="gap-1.5 rounded-full text-xs px-4 h-9 text-stone-600 hover:text-primary dark:text-stone-300"
                            >
                                <Shuffle className="w-3.5 h-3.5" />
                                {t("home.hero.randomQuestion", "Roll & Talk")}
                            </Button>
                        )}
                    </div>
                </div>

                {/* One quote per calendar day, for everyone signed in or out.
                    A direct child of this flex row, so it stacks under the CTA
                    on mobile and becomes a right-hand rail on desktop
                    (lg:ml-auto pushes it right, lg:items-end aligns it to the
                    bottom of the hero content). Inside the hero rather than
                    beside it, so it never becomes a second band competing with
                    the CTA. */}
                <DailyQuote />
            </div>
        </div>
    );
};

export { HomeHero };
export type { HomeHeroProps };

export const HomeHeroSkeleton = ({ className }: { className?: string }) => (
    <div
        className={cn(
            "relative overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-lg shadow-primary/5 dark:border-stone-700 dark:bg-stone-900 dark:shadow-none",
            "p-5 sm:p-6 lg:p-8",
            className
        )}
        aria-hidden="true"
    >
        <div className="space-y-3 animate-pulse">
            <div className="flex items-center gap-3">
                <div className="h-7 w-7 sm:h-8 sm:w-8 bg-stone-100 dark:bg-stone-800 rounded" />
                <div className="h-8 w-72 bg-stone-100 dark:bg-stone-800 rounded" />
            </div>
            <div className="h-4 w-96 max-w-full bg-stone-100 dark:bg-stone-800 rounded" />
            <div className="flex gap-2 pt-2">
                <div className="h-8 w-32 bg-stone-100 dark:bg-stone-800 rounded-full" />
                <div className="h-8 w-32 bg-stone-100 dark:bg-stone-800 rounded-full" />
            </div>
        </div>
    </div>
);
